import { HiFilter, HiPlus, HiTrash, HiX } from "react-icons/hi";
import type { ExportOption, ExportOptionKind } from "../../types/exports";
import { ExportOptionSelect } from "./ExportOptionSelect";
import { triggerClass, usePopover } from "./usePopover";

export const FIELD_ORDER: ExportOptionKind[] = [
  "providers",
  "contract_types",
  "teams",
  "workers",
];

export const FIELD_LABELS: Record<ExportOptionKind, string> = {
  providers: "Anbieter",
  contract_types: "Vertragsart",
  teams: "Team",
  workers: "Mitarbeitende",
};

const PLACEHOLDERS: Record<ExportOptionKind, string> = {
  providers: "Anbieter wählen …",
  contract_types: "Alle Vertragsarten",
  teams: "Teams wählen …",
  workers: "Mitarbeitende wählen …",
};

interface Props {
  rows: ExportOptionKind[];
  selections: Record<ExportOptionKind, number[]>;
  options: Record<ExportOptionKind, ExportOption[]>;
  loading: Record<ExportOptionKind, boolean>;
  errors: Partial<Record<ExportOptionKind, string>>;
  activeCount: number;
  onSelect: (kind: ExportOptionKind, ids: number[]) => void;
  onAddRow: (kind: ExportOptionKind) => void;
  onRemoveRow: (kind: ExportOptionKind) => void;
  onReplaceRow: (from: ExportOptionKind, to: ExportOptionKind) => void;
  onReset: () => void;
}

const cellBorder = "border-gray-300 dark:border-gray-600";

export function ExportConditionPopover({
  rows,
  selections,
  options,
  loading,
  errors,
  activeCount,
  onSelect,
  onAddRow,
  onRemoveRow,
  onReplaceRow,
  onReset,
}: Props) {
  const popover = usePopover();
  const hasProviders = selections.providers.length > 0;
  const unused = FIELD_ORDER.filter((kind) => !rows.includes(kind));
  const nextField =
    unused.find((kind) => kind !== "contract_types" || hasProviders) ?? null;
  const hasSellerRow = rows.includes("teams") || rows.includes("workers");

  const prefix = (kind: ExportOptionKind, index: number) => {
    if (index === 0) return "Wo";
    // Teams and additional sellers are merged, everything else narrows.
    if (kind === "workers" && rows.includes("teams")) return "oder";
    return "und";
  };

  return (
    <div ref={popover.containerRef} className="relative">
      <button
        ref={popover.triggerRef}
        type="button"
        onClick={popover.toggle}
        aria-expanded={popover.open}
        aria-haspopup="dialog"
        className={`${triggerClass} ${
          activeCount > 0
            ? "bg-blue-50 text-blue-700 ring-blue-300 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-200 dark:ring-blue-700 dark:hover:bg-blue-900/60"
            : ""
        }`}
      >
        <HiFilter className="h-4 w-4" aria-hidden="true" />
        <span>Filter</span>
        {activeCount > 0 && (
          <span className="rounded-full bg-blue-700 px-1.5 py-0.5 text-[10px] leading-none text-white">
            {activeCount}
          </span>
        )}
      </button>

      {popover.open && (
        <>
          {/* Mobile: the panel is a bottom sheet over a dimmed page. */}
          <div
            className="fixed inset-0 z-30 bg-gray-900/40 sm:hidden"
            aria-hidden="true"
            onClick={popover.close}
          />
          <div
            role="dialog"
            aria-labelledby="export-condition-title"
            className="fixed inset-x-0 bottom-0 z-40 max-h-[85vh] overflow-y-auto rounded-t-xl bg-white p-4 shadow-xl ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-600 sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-2 sm:max-h-none sm:w-[min(760px,calc(100vw-2rem))] sm:overflow-visible sm:rounded-lg sm:shadow-lg"
          >
            <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
              <h2
                id="export-condition-title"
                className="text-sm font-semibold text-gray-900 dark:text-white"
              >
                Filter
              </h2>
              <button
                type="button"
                onClick={popover.close}
                aria-label="Filter schließen"
                className="rounded p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <HiX className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <p className="mb-2 mt-3 text-sm text-gray-600 dark:text-gray-300">
              Im Export unterschriebene Verträge anzeigen,
            </p>

            <ul className="space-y-2">
              {rows.map((kind, index) => {
                const fieldChoices = [
                  kind,
                  ...unused.filter((other) => other !== "providers"),
                ].sort(
                  (a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b)
                );
                const fieldId = `export-condition-${kind}`;
                const disabled = kind === "contract_types" && !hasProviders;
                return (
                  <li
                    key={kind}
                    className="grid grid-cols-[3rem_minmax(0,1fr)_2.25rem] items-center gap-y-1.5 sm:grid-cols-[3.5rem_10rem_7.5rem_minmax(0,1fr)_2.25rem] sm:gap-y-0"
                  >
                    <span className="pr-2 text-sm text-gray-600 dark:text-gray-300">
                      {prefix(kind, index)}
                    </span>

                    <div
                      className={`min-w-0 rounded-lg border sm:rounded-r-none ${cellBorder}`}
                    >
                      <label htmlFor={`${fieldId}-field`} className="sr-only">
                        Bedingung {index + 1}: Feld
                      </label>
                      <select
                        id={`${fieldId}-field`}
                        value={kind}
                        disabled={kind === "providers"}
                        onChange={(event) =>
                          onReplaceRow(
                            kind,
                            event.target.value as ExportOptionKind
                          )
                        }
                        className="h-9 w-full rounded-lg border-0 bg-white py-0 pl-2.5 pr-8 text-sm text-gray-900 focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:cursor-default disabled:opacity-100 dark:bg-gray-700 dark:text-white sm:rounded-r-none"
                      >
                        {kind === "providers" ? (
                          <option value="providers">
                            {FIELD_LABELS.providers}
                          </option>
                        ) : (
                          fieldChoices.map((choice) => (
                            <option
                              key={choice}
                              value={choice}
                              disabled={
                                choice === "contract_types" && !hasProviders
                              }
                            >
                              {FIELD_LABELS[choice]}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="row-span-2 flex justify-end sm:order-last sm:row-span-1">
                      {kind === "providers" ? (
                        <span
                          className="flex h-9 w-9 items-center justify-center"
                          title="Pflichtfeld"
                        >
                          <span className="sr-only">Pflichtbedingung</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onRemoveRow(kind)}
                          aria-label={`Bedingung ${FIELD_LABELS[kind]} entfernen`}
                          title="Bedingung entfernen"
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-red-400"
                        >
                          <HiTrash className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    <span
                      className={`col-start-2 hidden h-9 items-center border-y border-r bg-gray-50 px-2.5 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300 sm:col-start-auto sm:flex ${cellBorder}`}
                    >
                      ist eine von
                    </span>

                    <div
                      className={`col-start-2 min-w-0 rounded-lg border sm:col-start-auto sm:rounded-l-none sm:rounded-r-lg sm:border-l-0 ${cellBorder}`}
                    >
                      <ExportOptionSelect
                        id={`${fieldId}-value`}
                        label={FIELD_LABELS[kind]}
                        placeholder={
                          disabled
                            ? "Zuerst Anbieter wählen"
                            : PLACEHOLDERS[kind]
                        }
                        options={options[kind]}
                        selected={selections[kind]}
                        onChange={(ids) => onSelect(kind, ids)}
                        disabled={disabled}
                        loading={loading[kind]}
                        error={errors[kind]}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              {nextField ? (
                <button
                  type="button"
                  onClick={() => onAddRow(nextField)}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <HiPlus className="h-4 w-4" aria-hidden="true" />
                  Bedingung hinzufügen
                </button>
              ) : (
                <span />
              )}
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-md px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                >
                  Alle Filter zurücksetzen
                </button>
              )}
            </div>

            <p className="mt-3 border-t border-gray-200 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              {hasSellerRow
                ? "Teams und zusätzlich ausgewählte Mitarbeitende werden zusammengeführt. Die CSV-Spalte „Ausgewählte Teams“ zeigt die Filterauswahl, nicht die Teammitgliedschaft jeder Person."
                : "Ohne Team- oder Mitarbeitenden-Bedingung werden alle Mitarbeitenden Ihrer Organisation exportiert."}{" "}
              Die Standardprovision verwendet die aktuelle Provisionsstufe und
              den zum Unterschriftsdatum gültigen Satz; sie ist keine
              historische Auszahlungsabrechnung.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
